import React from "react";
import "./Image.scss";

/**
 * Props for the Image component.
 *
 * @description The Image component is a wrapper around the HTML img element
 * with consistent styling and click handling support.
 *
 * @property {string} [image] - The source URL of the image.
 * @property {string} [name] - The alt text for the image (accessibility).
 * @property {number} [width=48] - The width of the image in pixels.
 * @property {number} [height=48] - The height of the image in pixels.
 * @property {string} [className] - Additional CSS class names.
 * @property {() => void} [onClick] - Click event handler.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {React.HtmlHTMLAttributes<HTMLImageElement>} ...attr - Additional HTML img attributes.
 *
 * @example
 * // Basic image
 * <Image
 *   image="/path/to/image.png"
 *   name="Profile picture"
 *   width={100}
 *   height={100}
 * />
 *
 * @example
 * // Clickable image
 * <Image
 *   image="/path/to/thumbnail.png"
 *   name="Product thumbnail"
 *   width={200}
 *   height={150}
 *   onClick={handleImageClick}
 * />
 *
 * @example
 * // Image with additional attributes
 * <Image
 *   image="/path/to/photo.png"
 *   name="Photo"
 *   width={300}
 *   height={200}
 *   loading="lazy"
 *   className="responsive-image"
 * />
 */
interface Props extends React.HtmlHTMLAttributes<HTMLImageElement> {
  image?: string;
  name?: string;
  width?: number;
  height?: number;
  className?: string;
  onClick?: () => void;
  testId?: string;
}

/**
 * An image component with consistent styling and click support.
 *
 * @description Image renders an HTML img element with configurable dimensions,
 * alt text, and click handling. It extends standard HTML img attributes,
 * allowing additional props like loading, decoding, etc.
 *
 * Features:
 * - Configurable width and height
 * - Alt text for accessibility
 * - Click event support
 * - Extends standard HTML img attributes
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} An img element with the configured properties
 *
 * @see Props for all available properties
 */
export const Image: React.FC<Props> = (props) => {
  const {
    image = "",
    name = "",
    width = 48,
    height = 48,
    className,
    onClick,
    testId,
    ...attr
  } = props;

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <img
      className={className}
      src={image}
      alt={name}
      width={width}
      height={height}
      onClick={handleOnClick}
      data-testid={testId}
      {...attr}
    />
  );
};
